import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Trophy, Images, Search, ArrowUpDown, Plus, Filter, ChevronDown, ChevronUp, MapPin, Play, Video, Mic } from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { useChapterTimeline } from '../hooks/useChaptersData';
import { focalPosition } from '../utils/mediaLayout';
import { getMediaServeUrl } from '../config/mediaAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');
const resolveUrl = (url) => url && url.startsWith('/') ? `${API_ORIGIN}${url}` : url;
const CH_COLORS = ['#6B8E23', '#8B7355', '#556B2F', '#7B6B8E', '#8E6B23'];
const chColor = (i) => CH_COLORS[((i || 1) - 1) % CH_COLORS.length];

/* ─── Item Card ─── */
function ItemCard({ item, navigate }) {
  const isMemory = item.itemType === 'memory';
  const hasCover = !!item.cover_url || !!item.thumbnail_url;
  const badgeConfig = isMemory
    ? { label: '📝 Memory', bg: '#F0F5E8', fg: '#556B2F' }
    : { label: '⭐ Milestone', bg: '#FFF7E0', fg: '#B8860B' };

  const handleClick = () => {
    if (isMemory && item.life_event_id) navigate(`/stories/${item.life_event_id}`);
    else if (isMemory) navigate(`/my-life/memories/${item.id}`);
    else navigate(`/my-life/milestones/${item.id}`);
  };

  return (
    <div onClick={handleClick} style={{
      background: '#fff', borderRadius: 16, border: '1px solid #EDEBDD', overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(54,60,40,.05), 0 1px 3px rgba(54,60,40,.06)',
      cursor: 'pointer', transition: 'transform .15s, box-shadow .15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(54,60,40,.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(54,60,40,.05), 0 1px 3px rgba(54,60,40,.06)'; }}
    >
      {/* Cover or tinted panel */}
      <div style={{ height: 170, position: 'relative', overflow: 'hidden' }}>
        {item.media_type === 'audio' ? (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#F3EAFA,#EDF2FB)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#6B3FA0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Play size={20} style={{ marginLeft: 2 }} /></div>
            <div><div style={{ fontWeight: 700, fontSize: 14, color: '#2C2F26', fontFamily: FONT_B }}>Voice note</div>
            {item.duration && <div style={{ fontSize: 12, color: '#828A76', fontFamily: FONT_B }}>{Math.floor(item.duration/60)}:{String(Math.floor(item.duration%60)).padStart(2,'0')}</div>}</div>
          </div>
        ) : item.media_type === 'video' && hasCover ? (
          <>
            <img src={item.cover_url || item.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focalPosition(item), display: 'block', imageOrientation: 'from-image' }} loading="lazy" decoding="async" />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,.15)' }}><Play size={20} color="#2C2F26" style={{ marginLeft: 3 }} /></div>
            </div>
            {item.duration && <span style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(28,32,22,.6)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999, display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONT_B }}><Video size={12} /> {Math.floor(item.duration/60)}:{String(Math.floor(item.duration%60)).padStart(2,'0')}</span>}
          </>
        ) : hasCover ? (
          <img src={item.cover_url || item.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focalPosition(item), display: 'block', imageOrientation: 'from-image' }} loading="lazy" decoding="async" />
        ) : (
          <div style={{ width: '100%', height: '100%', background: isMemory ? 'linear-gradient(135deg,#EAF0DE,#DCE6C8)' : 'linear-gradient(135deg,#FFF7E0,#F5E8C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 20px' }}>
            <p style={{ fontFamily: FONT_H, fontStyle: 'italic', fontSize: 16, color: '#5A6150', textAlign: 'center', lineHeight: 1.4, margin: 0 }}>{(item.text || item.title || '').slice(0, 100)}{(item.text || '').length > 100 ? '...' : ''}</p>
          </div>
        )}
        {/* Type badge */}
        <span style={{ position: 'absolute', top: 10, left: 10, background: badgeConfig.bg, color: badgeConfig.fg, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, fontFamily: FONT_B }}>{badgeConfig.label}</span>
        {/* Photo count badge */}
        {item.photo_count > 1 && item.media_type !== 'video' && item.media_type !== 'audio' && (
          <span style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(28,32,22,.6)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999, display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONT_B }}>
            <Images size={12} /> {item.photo_count}
          </span>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '12px 16px 16px' }}>
        <h4 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 16, color: '#2C2F26', margin: '0 0 4px', lineHeight: 1.2 }}>
          {item.title || 'Untitled'}
        </h4>
        <div style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B }}>
          {item.year}{item.location ? ` · ${item.location}` : ''}
        </div>
      </div>
    </div>
  );
}

/* ─── Year Group ─── */
function YearGroup({ year, items, navigate, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, 6);
  const remaining = items.length - 6;

  return (
    <div style={{ marginBottom: 28 }} id={`year-${year}`}>
      {/* Year header */}
      <div onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', marginBottom: expanded ? 16 : 0,
        borderBottom: expanded ? '1px solid #EFEEE5' : 'none', paddingBottom: expanded ? 12 : 0,
      }}>
        <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 26, color: '#2C2F26', margin: 0 }}>{year}</h3>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#828A76', background: '#EFEEE5', padding: '2px 10px', borderRadius: 999, fontFamily: FONT_B }}>
          {items.length} this year
        </span>
        <div style={{ flex: 1 }} />
        {expanded ? <ChevronUp size={18} color="#828A76" /> : <ChevronDown size={18} color="#828A76" />}
      </div>

      {/* Cards grid */}
      {expanded && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }} className="chapter-items-grid">
            {visible.map(item => <ItemCard key={item.id} item={item} navigate={navigate} />)}
          </div>
          {remaining > 0 && !showAll && (
            <button onClick={() => setShowAll(true)} style={{
              display: 'block', width: '100%', textAlign: 'center', padding: '12px 0', marginTop: 12,
              background: '#FAFAF3', border: '1px solid #EFEEE5', borderRadius: 12,
              fontWeight: 700, fontSize: 14, color: '#556B2F', cursor: 'pointer', fontFamily: FONT_B,
            }}>
              Show {remaining} more from {year}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Main Chapter Page ─── */
export default function ChapterDetail() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { chapter, timeline, loading } = useChapterTimeline(chapterId);
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');

  // Flatten timeline (year-keyed object) into a flat items array
  const allItems = useMemo(() => {
    if (!timeline || typeof timeline !== 'object') return [];
    const items = [];
    Object.values(timeline).forEach(yearItems => {
      if (!Array.isArray(yearItems)) return;
      yearItems.forEach(item => {
        const d = item.data || item;
        items.push({
          id: d.id || item.id,
          title: d.title || '',
          text: d.text || '',
          year: item.year || d.year,
          location: d.location || '',
          itemType: item.type || (d.quick_pick_type ? 'milestone' : 'memory'),
          life_event_id: d.life_event || d.life_event_id,
          cover_url: resolveUrl(d.cover_url || d.thumbnail_url) || null,
          thumbnail_url: resolveUrl(d.thumbnail_url) || null,
          photo_count: d.photo_count || 0,
        });
      });
    });
    return items;
  }, [timeline]);

  // Filter + sort + search
  const filteredItems = useMemo(() => {
    let items = allItems;
    if (typeFilter === 'memories') items = items.filter(i => i.itemType === 'memory');
    else if (typeFilter === 'milestones') items = items.filter(i => i.itemType === 'milestone');
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i => (i.title || '').toLowerCase().includes(q) || (i.text || '').toLowerCase().includes(q));
    }
    items.sort((a, b) => sortOrder === 'newest' ? (b.year || 0) - (a.year || 0) : (a.year || 0) - (b.year || 0));
    return items;
  }, [allItems, typeFilter, sortOrder, searchQuery]);

  // Group by year
  const yearGroups = useMemo(() => {
    const groups = {};
    filteredItems.forEach(item => {
      const y = item.year || 'Unknown';
      if (!groups[y]) groups[y] = [];
      groups[y].push(item);
    });
    return Object.entries(groups).sort(([a], [b]) => sortOrder === 'newest' ? Number(b) - Number(a) : Number(a) - Number(b));
  }, [filteredItems, sortOrder]);

  const years = yearGroups.map(([y]) => y);
  const chIdx = chapter?.number || 1;
  const color = chColor(chIdx);
  const era = chapter ? `${chapter.start_year || '?'} – ${chapter.end_year || 'Present'}` : '';
  const totalMemories = allItems.filter(i => i.itemType === 'memory').length;
  const totalMilestones = allItems.filter(i => i.itemType === 'milestone').length;

  if (loading) return <HubLayout activeView="life"><div style={{ textAlign: 'center', padding: 60, color: '#828A76' }}>Loading chapter...</div></HubLayout>;

  return (
    <HubLayout activeView="life">
      {/* Hero */}
      <div style={{
        background: `linear-gradient(135deg, ${color}cc, ${color})`, color: '#fff',
        padding: '28px 40px 32px',
      }}>
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>
          <button onClick={() => navigate('/my-life')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.15)',
            border: '1px solid rgba(255,255,255,.25)', color: '#fff', fontSize: 14, fontWeight: 600,
            padding: '7px 14px', borderRadius: 999, cursor: 'pointer', fontFamily: FONT_B, marginBottom: 16,
          }}>
            <ArrowLeft size={16} /> Back to My Life
          </button>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', opacity: 0.8, marginBottom: 6, fontFamily: FONT_B }}>
                Chapter {chIdx}
              </div>
              <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 40, margin: '0 0 6px', lineHeight: 1.08 }}>
                {chapter?.title || 'Chapter'}
              </h1>
              <div style={{ fontSize: 16, opacity: 0.85, fontFamily: FONT_B }}>{era}</div>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              {[{ icon: <BookOpen size={18} />, n: totalMemories, l: 'Memories' },
                { icon: <Trophy size={18} />, n: totalMilestones, l: 'Milestones' }]
                .map(s => (
                  <div key={s.l} style={{ textAlign: 'center', background: 'rgba(255,255,255,.12)', borderRadius: 12, padding: '10px 18px', minWidth: 80 }}>
                    {s.icon}
                    <div style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 22, marginTop: 2 }}>{s.n}</div>
                    <div style={{ fontSize: 12, opacity: 0.8, fontFamily: FONT_B }}>{s.l}</div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '22px 32px 80px' }}>
        {/* Sticky toolbar */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10, background: '#F5F5DC',
          padding: '12px 0 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          borderBottom: '1px solid #EFEEE5', marginBottom: 24,
        }}>
          {/* Type filter */}
          <div style={{ display: 'flex', background: '#EDE8CD', borderRadius: 999, padding: 3, gap: 2 }}>
            {[{ key: 'all', label: `All ${allItems.length}` }, { key: 'memories', label: 'Memories' }, { key: 'milestones', label: 'Milestones' }].map(f => (
              <button key={f.key} onClick={() => setTypeFilter(f.key)} style={{
                padding: '7px 14px', borderRadius: 999, fontSize: 14, fontWeight: typeFilter === f.key ? 700 : 600,
                background: typeFilter === f.key ? '#556B2F' : 'transparent',
                color: typeFilter === f.key ? '#fff' : '#5A6150', border: 'none', cursor: 'pointer', fontFamily: FONT_B,
              }}>{f.label}</button>
            ))}
          </div>

          {/* Sort */}
          <button onClick={() => setSortOrder(s => s === 'newest' ? 'oldest' : 'newest')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: '1px solid #E6E4D8',
            color: '#5A6150', fontSize: 14, fontWeight: 600, padding: '7px 14px', borderRadius: 999, cursor: 'pointer', fontFamily: FONT_B,
          }}>
            <ArrowUpDown size={14} /> {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
          </button>

          {/* Search */}
          <div style={{ position: 'relative', flex: '0 1 200px' }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#828A76' }} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search..."
              style={{ width: '100%', padding: '7px 12px 7px 32px', border: '1px solid #E6E4D8', borderRadius: 999, fontSize: 14, fontFamily: FONT_B, outline: 'none', background: '#fff' }} />
          </div>

          {/* Add */}
          <button onClick={() => navigate(`/tell-a-story?chapter=${chapterId}`)} style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
            background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14,
            padding: '8px 16px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B,
          }}>
            <Plus size={15} /> Add to chapter
          </button>
        </div>

        {/* Year-jump chips */}
        {years.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
            {years.map(y => (
              <button key={y} onClick={() => document.getElementById(`year-${y}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                style={{ background: '#fff', border: '1px solid #E6E4D8', color: '#5A6150', fontSize: 13, fontWeight: 600, padding: '5px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: FONT_B }}>
                {y}
              </button>
            ))}
          </div>
        )}

        {/* Year groups */}
        {yearGroups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#828A76' }}>
            <p style={{ fontSize: 18, fontFamily: FONT_H }}>No items in this chapter yet.</p>
            <button onClick={() => navigate(`/tell-a-story?chapter=${chapterId}`)} style={{
              background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 15,
              padding: '12px 24px', borderRadius: 999, border: 'none', cursor: 'pointer', marginTop: 12,
            }}>Add your first story</button>
          </div>
        ) : (
          yearGroups.map(([year, items], i) => (
            <YearGroup key={year} year={year} items={items} navigate={navigate} defaultExpanded={i < 3} />
          ))
        )}
      </div>

      <style>{`
        @media (max-width: 1040px) { .chapter-items-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 680px) { .chapter-items-grid { grid-template-columns: 1fr !important; } }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
      `}</style>
    </HubLayout>
  );
}
